import { DecimalPipe } from '@angular/common';
import { Directive, ElementRef, forwardRef, HostListener, Input, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { Unit } from 'src/app/models';

export enum Algorithm {
  Round = 0,
  Floor = 1,
  Ceil = 2,
}

export const NUMBER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => FormatUnitNumberDirective),
  multi: true,
};

@Directive({
  selector:
    'input[type=number][formControlName][formatUnitNumber],input[type=number][formControl][formatUnitNumber],input[type=number][ngModel][formatUnitNumber]',
  host: { '(blur)': 'onTouched()' },
  providers: [NUMBER_VALUE_ACCESSOR],
})
export class FormatUnitNumberDirective implements ControlValueAccessor {
  onChange: any;
  onTouched: any;

  @Input('numberFormat') public format: string = '1.0-1';
  @Input('unit') public unit: Unit | null | undefined = undefined;
  @Input('numberFormatAlgorithm') public formatAlgorithm = Algorithm.Round;
  @Input('defaultValueToZero') public defaultValueToZero: boolean = true;

  constructor(private _renderer: Renderer2, private _elementRef: ElementRef, private decimalPipe: DecimalPipe) {}

  @HostListener('input', ['$event.target.value'])
  input(value: string) {
    // here to notify Angular Validators
    this.onChange(value);
  }

  /**
   * Sets the "value" property on the input element.
   * @nodoc
   */
  writeValue(value: number): void {
    const formattedValue = this.formatValue(value);
    this.setProperty('value', formattedValue);
  }

  /**
   * Registers a function called when the control value changes.
   * @nodoc
   */
  registerOnChange(fn: (_: number | null) => void): void {
    this.onChange = (value: string) => {
      fn(value == '' ? null : parseFloat(value));
    };
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.setProperty('disabled', isDisabled);
  }

  protected setProperty(key: string, value: any): void {
    this._renderer.setProperty(this._elementRef.nativeElement, key, value);
  }

  private formatValue(value: number) {
    if (this.unit !== undefined && this.unit !== null) {
      if (this.unit.id === 17) {
        this.format = '1.0-3';
      } else if (this.unit.category === 'LEISTUNG' && value > 10) {
        this.format = '1.0-1';
      }
    }

    if ((value === null || value === undefined) && this.defaultValueToZero === false) {
      return '';
    } else if (value === null || value === undefined) {
      value = 0;
    }

    switch (this.formatAlgorithm) {
      case Algorithm.Floor: {
        value = Math.floor(value);
        break;
      }
      case Algorithm.Ceil: {
        value = Math.ceil(value);
        break;
      }
      default: {
        break;
      }
    }

    const formattedValue = this.decimalPipe.transform(value, this.format, 'en')?.replace(',', '');
    const normalizedValue = formattedValue == null ? '' : formattedValue;
    return normalizedValue;
  }
}
